import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import VoiceTextInput from '../components/VoiceTextInput';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import PageHeader from '../components/PageHeader';
import Footer from '../components/Footer';
import { authFetch } from '../lib/authFetch';

const AXIOM_LOGO = 'https://customer-assets.emergentagent.com/job_sponsor-bot-clinical/artifacts/kgiafx8p_AxiomLogo.png';
const STICKMAN_IMAGE = 'https://customer-assets.emergentagent.com/job_c2b961a8-ea8c-4dbb-ad47-155fcf8e5075/artifacts/tot5eq2a_Stone2%20Stickman1.jpeg';

// Text box positions around the stickman (percentage-based for responsiveness)
const TEXT_BOXES = [
  { id: 'health_diet', label: 'Health & Diet', labelColor: 'text-orange-600', position: { top: '5%', left: '5%', width: '35%' } },
  { id: 'job_finances', label: 'Job & Finances', labelColor: 'text-orange-600', position: { top: '5%', right: '5%', width: '35%' } },
  { id: 'friends', label: 'Friends', labelColor: 'text-orange-600', position: { top: '35%', left: '0%', width: '30%' } },
  { id: 'home', label: 'Home', labelColor: 'text-orange-600', position: { top: '35%', right: '0%', width: '30%' } },
  { id: 'relationships', label: 'Relationships', labelColor: 'text-orange-600', position: { top: '65%', left: '0%', width: '30%' } },
  { id: 'family', label: 'Family', labelColor: 'text-orange-600', position: { top: '65%', right: '0%', width: '30%' } },
  { id: 'name_picture', label: 'Your Name', labelColor: 'text-blue-600', position: { top: '18%', left: '50%', transform: 'translateX(-50%)', width: '25%' }, isCenter: true }
];

function StickmanAssessmentPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    health_diet: '',
    job_finances: '',
    friends: '',
    home: '',
    relationships: '',
    family: '',
    name_picture: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [activeBox, setActiveBox] = useState(null);

  useEffect(() => {
    fetchExistingData();
  }, []);

  const fetchExistingData = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/stickman-assessment`);
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setFormData(prev => ({ ...prev, ...data }));
        }
      }
    } catch (error) {
      console.error('Error fetching stickman data:', error);
    }
  };

  const handleChange = (id, value) => {
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/stickman-assessment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Stickman assessment saved!');
      } else {
        toast.error('Failed to save');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('An error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 pb-20">
      <PageHeader 
        title="Stickman Assessment"
        parentSection={{ label: 'Self-Work', route: '/self-work' }}
      />

      <main className="pt-24 px-4 pb-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Stickman Assessment
            </h1>
            <p className="text-sm text-blue-100">
              Fill in the boxes around the stickman with your personal information
            </p>
          </div>

          {/* Interactive Stickman Container */}
          <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-xl relative" data-testid="stickman-container">
            {/* Title */}
            <h2 className="text-center text-2xl font-bold text-blue-700 mb-4">Stickman 1</h2>

            {/* Stickman Layout */}
            <div className="relative" style={{ minHeight: '700px' }}>
              {/* SVG Stickman in the center */}
              <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none" style={{ width: '120px', zIndex: 1 }}>
                <svg viewBox="0 0 100 200" className="w-full h-auto">
                  {/* Head */}
                  <circle cx="50" cy="25" r="20" fill="none" stroke="#333" strokeWidth="3" />
                  {/* Body */}
                  <line x1="50" y1="45" x2="50" y2="120" stroke="#333" strokeWidth="3" />
                  {/* Left Arm */}
                  <line x1="50" y1="60" x2="15" y2="40" stroke="#333" strokeWidth="3" />
                  {/* Right Arm */}
                  <line x1="50" y1="60" x2="85" y2="40" stroke="#333" strokeWidth="3" />
                  {/* Left Leg */}
                  <line x1="50" y1="120" x2="25" y2="180" stroke="#333" strokeWidth="3" />
                  {/* Right Leg */}
                  <line x1="50" y1="120" x2="75" y2="180" stroke="#333" strokeWidth="3" />
                </svg>
              </div>

              {/* Text Boxes Around Stickman */}
              {TEXT_BOXES.map((box) => (
                <div
                  key={box.id}
                  className={`absolute transition-all duration-200 ${box.isCenter ? 'z-10' : 'z-0'}`}
                  style={{
                    ...box.position
                  }}
                >
                  <label className={`block text-sm font-bold uppercase tracking-wide mb-1 ${box.labelColor}`}>
                    {box.label}
                  </label>
                  <div 
                    className={`
                      border-2 rounded-lg overflow-hidden transition-all
                      ${activeBox === box.id 
                        ? 'border-blue-500 shadow-lg shadow-blue-200' 
                        : 'border-gray-300 hover:border-blue-300'
                      }
                    `}
                  >
                    {box.isCenter ? (
                      <Textarea
                        value={formData[box.id] || ''}
                        onChange={(e) => handleChange(box.id, e.target.value)}
                        onFocus={() => setActiveBox(box.id)}
                        onBlur={() => setActiveBox(null)}
                        placeholder="Enter your name..."
                        rows={1}
                        className="w-full resize-none border-0 focus:ring-0 text-sm text-center font-semibold text-blue-800"
                        data-testid={`stickman-${box.id}`}
                      />
                    ) : (
                      <VoiceTextInput
                        value={formData[box.id] || ''}
                        onChange={(v) => handleChange(box.id, v)}
                        placeholder={`Notes about ${box.label.toLowerCase()}...`}
                        rows={4}
                        className="w-full resize-none border-0 focus:ring-0 text-sm text-gray-700"
                        testId={`stickman-${box.id}`}
                      />
                    )}
                  </div>
                </div>
              ))}

              {/* Connecting Lines (decorative) */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
                <defs>
                  <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#e5e7eb" />
                  </marker>
                </defs>
                {/* Lines from center to corners */}
                <line x1="50%" y1="50%" x2="20%" y2="15%" stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4" />
                <line x1="50%" y1="50%" x2="80%" y2="15%" stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4" />
                <line x1="50%" y1="50%" x2="10%" y2="45%" stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4" />
                <line x1="50%" y1="50%" x2="90%" y2="45%" stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4" />
                <line x1="50%" y1="50%" x2="10%" y2="75%" stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4" />
                <line x1="50%" y1="50%" x2="90%" y2="75%" stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4" />
              </svg>
            </div>
          </div>

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="w-full mt-6 bg-white text-blue-700 hover:bg-blue-50 rounded-xl h-14 font-bold text-sm uppercase tracking-wide shadow-lg"
            data-testid="save-stickman-btn"
          >
            <Save className="w-5 h-5 mr-2" />
            {isSaving ? 'Saving...' : 'Save Assessment'}
          </Button>

          <p className="text-xs text-blue-200 text-center mt-4">
            This assessment helps identify areas of your life affected by addiction
          </p>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default StickmanAssessmentPage;
